import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';

import {UserService} from '@app/core';
import {
  AbstractItem,
  AccountingCloseTask,
  AccountSetting, ApprovalStatusType,
  BLOB_RESPONSE_TYPE,
  Calendar,
  CalendarType,
  CommonTransformationService,
  DefaultPermissionSetting, DocFile,
  FileService,
  IdNamePair,
  ItemCatalog, LeaveDetail,
  NoTitlecasePartialTransactionType,
  OrgAchDetail,
  OrgAsset,
  OrgBeneficialOwner,
  OrgDocument,
  OrgFile,
  OrgPolicy,
  OrgUnit,
  PartnerAchDetail,
  ProjectRevenueBalance,
  ProjectTaskBalance,
  QueryResult, SalesTaxRate, Transaction, User,
  UserIdentity, WorkitemApprovalSummary
} from '@app/shared';

import {ImportFile, ImportTemplate} from './data-import/data-import';

import {DocAttribute} from './document/doc-attribute/doc-attribute';
import {DocType} from '@app/shared/classes/doc-type';

import {Realm} from './org-info/org-info';

import {DocNumber} from './system/doc-number/doc-number';

import {GroupAction, KeycloakGroup, KeycloakPassword} from './security/keycloak';
import {AdditionalInfo} from './system/additional-info/additional-info';
import {CorrespondenceCategory} from './system/correspondence-category/correspondence-category';
import {PdfTemplate} from './system/pdf-template/pdf-template';
import {RefCode} from './system/ref-code/ref-code';
import {RefDomain} from './system/ref-domain/ref-domain';
import {SystemParam} from './system/system-param/system-param';
import {ToDoLane} from './system/to-do-lane/to-do-lane';

import {TxnTypes} from './workflow/txn-types/txn-types';
import {MessageTemplate} from './workflow/message-template/message-template';
import {AuthorizationPolicy} from './workflow/authorization-policy/authorization-policy';
import {EscalationPolicy} from './workflow/escalation-policy/escalation-policy';
import {DelegationPolicy} from './workflow/delegation-policy/delegation-policy';
import {Workgroup} from './workflow/workgroup/workgroup';
import {ApprovalPolicyPayload, ApproverPayload} from './workflow/approval-policy/approval-policy';
import {AssignmentHandler} from "@app/solution-manager/workflow/assignment-handler/AssignmentHandler";
import {OrgPayrollTax} from "@app/solution-manager/document/org-payroll-tax-form/org-payroll-tax";
import {ComplianceAgency} from "@app/solution-manager/system/compliance-agency/compliance-agency";
import {AchCustomer} from "@app/shared/classes/ach-customer";
import {SettingValidationSummary, ValidationCategoryType} from "@app/solution-manager/org-setup-validation/org-setup-validation";
import {TxnType} from "@app/solution-manager/solution-manager";
import {AdminServiceInfo, HttpMethod} from "@app/solution-manager/system/admin-services/admin-services";
import {map} from "rxjs/operators";
import {SelectItem} from "primeng/api";
import {StatusPolicyPayload} from "@app/solution-manager/workflow/status-policy/status-policy";
import {PerDiemRate} from "@app/shared/classes/perdiem-rate";
import {ApprovableTransaction} from "@app/solution-manager/transaction-validation/transaction-validation";
import {plainToClass} from "class-transformer";
import {LeaveProcessingInfo} from "@app/finance/time/timesheet-admin/timesheet-admin";

@Injectable()
export class SolutionManagerDataService {

  constructor(private http: HttpClient, private user: UserService, private transform: CommonTransformationService, private fileService: FileService) {
  }

  private enumURL = `/utils/enum-pairs`;

  urlWithOrgId(url: string, orgId?): string {
    let urlWithOrgId: string = url;
    if (orgId) {
      urlWithOrgId += orgId;
    } else {
      urlWithOrgId += this.user.managingOrgId;
    }
    return urlWithOrgId;
  }

  getAuthorizedOrgsToManage(orgId?: number): Observable<SelectItem<OrgUnit>[]> {
    let url = this.urlWithOrgId(`/solution/authorized-orgs?orgId=`, orgId);

    return this.http.get(url).pipe(map(res => {
      return <SelectItem<OrgUnit>[]>this.transform.convertToDropDown('businessName', res['list'], null, true);
    }));
  }

  addUser(user: UserIdentity, orgId?: number): Observable<UserIdentity> {
    let url = this.urlWithOrgId(`/solution/user?orgId=`, orgId);

    return this.http.post<UserIdentity>(url, user);
  }

  updateUser(user: UserIdentity, orgId?: number): Observable<UserIdentity> {
    let url = this.urlWithOrgId(`/solution/users/${user.id}?orgId=`, orgId);

    return this.http.put<UserIdentity>(url, user);
  }

  deleteUser(userId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/users/${userId}?orgId=`, orgId);

    return this.http.delete<Response>(url);
  }

  getUserByName(name: string, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/user-lookup/name-search?name=${name}&orgId=`, orgId);

    return this.http.get<QueryResult>(url);
  }

  sendPasswordNotification(selectedUserIds, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/users/send-password-infos?orgId=`, orgId);
    return this.http.post<Response>(url, selectedUserIds);
  }

  getOverSightUser(orgId?: number): Observable<User> {
    let url = this.urlWithOrgId(`/solution/users/over-sight-user?orgId=`, orgId);
    return this.http.get<User>(url);
  }

  updateOverSightUser(userId: number): Observable<User> {
    let url = this.urlWithOrgId(`/solution/users/over-sight-user?userId=${userId}&orgId=`);
    return this.http.post<User>(url, {});
  }

  // manage user password and verification

  resetUserPassword(userId: number, password: KeycloakPassword, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/users/${userId}/reset-password?orgId=`, orgId);
    return this.http.put<Response>(url, password);
  }

  // manage user membership to idm group

  getGroups(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/idm-groups?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getUsersGroup(userId: number, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/users/${userId}/idm-groups?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  updateUserInGroup(userId: number, group: KeycloakGroup[], groupAction: GroupAction, orgId?: number): Observable<KeycloakGroup[]> {
    let url = this.urlWithOrgId(`/solution/users/${userId}/idm-groups?&groupAction=${groupAction}&orgId=`, orgId);
    return this.http.put<KeycloakGroup[]>(url, group);
  }

  addZoomUser(user: UserIdentity): Observable<Response> {
    const url = `/connect/zoomuser?email=${user.workEmail}`;
    return this.http.get<Response>(url);
  }

  deleteNewZoomUser(user: UserIdentity): Observable<Response> {
    const url = `/connect/zoomuser?email=${user.workEmail}`;
    return this.http.delete<Response>(url);
  }

  // approver policy data services

  getApproverDropDown(orgId?: number): Observable<SelectItem[]> {
    let url = this.urlWithOrgId(`/solution/approvers?orgId=`, orgId);
    return this.http.get(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res['list'], null, true);
    }));
  }

  getApprovalPolicyDropDown(orgId?: number): Observable<SelectItem<ApprovalPolicyPayload>[]> {
    let url = this.urlWithOrgId(`/solution/approval-policies?orgId=`, orgId);
    return this.http.get(url).pipe(map(res => {
      return <SelectItem<ApprovalPolicyPayload>[]>this.transform.convertToDropDown("name", res['list']);
    }));
  }

  getStatusPolicyDropDown(orgId?: number): Observable<SelectItem<StatusPolicyPayload>[]> {
    let url = this.urlWithOrgId(`/solution/status-policies?orgId=`, orgId);
    return this.http.get(url).pipe(map(res => {
      return <SelectItem<StatusPolicyPayload>[]>this.transform.convertToDropDown("name", res['list']);
    }));
  }

  getSystemParamTypes(orgId?: number): Observable<any> {
    // FixMe jC: fix the URL
    let url = this.urlWithOrgId(`/solution/status-policies?orgId=`, orgId);
    return this.http.get(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res['list']);
    }));
  }

  getTxnTypeDropDown(orgId?: number): Observable<SelectItem[]> {
    let url = this.urlWithOrgId(`/solution/txn-types?orgId=`, orgId);
    return this.http.get(url).pipe(map(res => {
      return this.transform.convertToDropDown("name", res['list']);
    }));
  }

  // additional info data services

  getAdditoinalInfos(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/additional-info?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveAdditionalInfos(additionalInfos: AdditionalInfo[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/additional-info?orgId=`, orgId);
    return this.http.post<QueryResult>(url, additionalInfos);
  }

  // authorization policy data services

  getAuthorizationPolicy(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/auth-policy?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  deleteAuthPolicy(policyId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/auth-policy?policyId=${policyId}&orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  saveAuthPolicy(policy: AuthorizationPolicy, orgId?: number): Observable<AuthorizationPolicy> {
    let url = this.urlWithOrgId(`/solution/auth-policy?orgId=`, orgId);
    return this.http.post<AuthorizationPolicy>(url, policy);
  }

  // delegation policy data services

  getDelegPolicy(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/delegation-policy?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  deleteDelegPolicy(policyId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/delegation-policy?policyId=${policyId}&orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  saveDelegPolicy(policy: DelegationPolicy, orgId?: number): Observable<DelegationPolicy> {
    let url = this.urlWithOrgId(`/solution/delegation-policy?orgId=`, orgId);
    return this.http.post<DelegationPolicy>(url, policy);
  }

  // escalation policy data services

  getEscPolicy(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/escalation-policy?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  deleteEscPolicy(policyId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/escalation-policy?policyId=${policyId}&orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  saveEscPolicy(policy: EscalationPolicy, orgId?: number): Observable<EscalationPolicy> {
    let url = this.urlWithOrgId(`/solution/escalation-policy?orgId=`, orgId);
    return this.http.post<EscalationPolicy>(url, policy);
  }

  // correspdongin category data services

  getCorrCategories(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/correspondence-categories?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveCorrCategories(correspCategories: CorrespondenceCategory[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/correspondence-categories?orgId=`, orgId);
    return this.http.post<QueryResult>(url, correspCategories);
  }

  // doc attribute data services

  getDocAttributes(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/attribute-names?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveDocAttributes(docAttributes: DocAttribute[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/attribute-names?orgId=`, orgId);
    return this.http.post<QueryResult>(url, docAttributes);
  }

  // doc numbering data services

  getDocNumbers(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/doc-numbers?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveDocNumbers(docNumbers: DocNumber[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/doc-numbers?orgId=`, orgId);
    return this.http.post<QueryResult>(url, docNumbers);
  }

  // doc type data services

  getDocTypes(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/doc-types?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveDocTypes(docTypes: DocType[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/doc-types?orgId=`, orgId);
    return this.http.post<QueryResult>(url, docTypes);
  }

  // import data services

  getImportTemplates(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/import-templates?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getProcessedImportTemplate(importTemplateId: number, orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/import-templates/download/${importTemplateId}?orgId=`, orgId);
    return this.http.get(url, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});
  }

  getProcessedImportTemplateThenSave(importTemplateId: number, orgId?: number) {
    this.getProcessedImportTemplate(importTemplateId, orgId).subscribe(data => {
      this.fileService.saveFileToLocalStorage(data.fileName, data);
    });
  }

  saveImportTemplate(importTemplate: ImportTemplate, orgId?: number): Observable<ImportTemplate> {
    let url = `/solution/import-templates`;
    if (importTemplate.id) {
      url += this.urlWithOrgId(`/${importTemplate.id}?orgId=`, orgId);
      return this.http.put<ImportTemplate>(url, importTemplate);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<ImportTemplate>(url, importTemplate);
    }
  }

  deleteImportTemplate(importTemplate: ImportTemplate, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/import-templates/${importTemplate.id}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  deleteDataImportTemplate(importTemplate: ImportTemplate, orgId?: number): Observable<Response> {
    //FixMe need to fix the URL
    let url = this.urlWithOrgId(`/solution/import-templates/${importTemplate.id}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  getImportFiles(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/import-files?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveImportFile(importFile: ImportFile, orgId?: number): Observable<ImportFile> {
    let url = `/solution/import-files`;
    if (importFile.id) {
      url += this.urlWithOrgId(`/${importFile.id}?orgId=`, orgId);
      return this.http.put<ImportFile>(url, importFile);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<ImportFile>(url, importFile);
    }
  }

  deleteDataImportFile(importFile: ImportFile, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/import-files/${importFile.id}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  processDataImport(importFile: ImportFile, orgId?: number): Observable<ImportFile> {
    let url = this.urlWithOrgId(`/solution/import-files/process/${importFile.id}?orgId=`, orgId);
    return this.http.put<ImportFile>(url, null);
  }

  getImportFileInSQL(importFile: ImportFile, orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/import-files/download/sql/${importFile.id}?orgId=`, orgId);
    return this.http.get(url, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});
  }

  getImportFileInSQLThenSave(importFile: ImportFile, orgId?: number) {
    this.getImportFileInSQL(importFile, orgId).subscribe(data => {
      this.fileService.saveFileToLocalStorage(data.fileName, data);
    });
  }

  // org asset data services

  getOrgAssets(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/org-assets/search?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveOrgAsset(orgAsset: OrgAsset, orgId?: number): Observable<OrgAsset> {
    let url = `/solution/org-assets`;
    if (orgAsset.id) {
      url += this.urlWithOrgId(`/${orgAsset.id}?orgId=`, orgId);
      return this.http.put<OrgAsset>(url, orgAsset);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<OrgAsset>(url, orgAsset);
    }
  }

  deleteOrgAsset(orgAsset: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/org-assets/${orgAsset}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  getOrgPayrollTax(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/org-payroll-tax-form?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  // ToDo jC: change the class name from OrgPayrollTax to OrgPayrollTaxForm
  saveOrgPayrollTax(orgPayrollTax: OrgPayrollTax, orgId?: number): Observable<OrgPayrollTax> {
    let url = `/solution/org-payroll-tax-form`;
    if (orgPayrollTax.id) {
      url += this.urlWithOrgId(`/${orgPayrollTax.id}?orgId=`, orgId);
      return this.http.put<OrgPayrollTax>(url, orgPayrollTax);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<OrgPayrollTax>(url, orgPayrollTax);
    }
  }

  deleteOrgPayrollTax(policyId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/org-payroll-tax-form/${policyId}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  // org policy data services

  getOrgPolicies(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/org-policies?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveOrgPolicy(orgPolicy: OrgPolicy, orgId?: number): Observable<OrgPolicy> {
    let url = `/solution/org-policies`;
    if (orgPolicy.id) {
      url += this.urlWithOrgId(`/${orgPolicy.id}?orgId=`, orgId);
      return this.http.put<OrgPolicy>(url, orgPolicy);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<OrgPolicy>(url, orgPolicy);
    }
  }

  deleteOrgPolicy(policyId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/org-policies/${policyId}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  // org document data services

  getOrgDocuments(docId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/org-documents?orgId=`, docId);
    return this.http.get<QueryResult>(url);
  }

  saveOrgDocument(orgDocument: OrgDocument, orgId?: number): Observable<OrgDocument> {
    let url = `/solution/org-documents`;
    if (orgDocument.id) {
      url += this.urlWithOrgId(`/${orgDocument.id}?orgId=`, orgId);
      return this.http.put<OrgDocument>(url, orgDocument);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<OrgDocument>(url, orgDocument);
    }
  }

  deleteOrgDocument(docId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/org-documents/${docId}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  getOrgFile(fileId: number, orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/files/${fileId}?orgId=`, orgId);
    return this.http.get(url, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});
  }

  getOrgFileThenSave(fileId: number, orgId?: number) {
    this.getOrgFile(fileId, orgId).subscribe(data => {
      this.fileService.saveFileToLocalStorage(data.fileName, data);
    });
  }

  // message template data services

  getMessageTemplates(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/message-templates?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  // Todo jC: define Message type
  renderMessageTemplate(msgTemplate, transaction, orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/message-templates/render?transaction=${transaction}&orgId=`, orgId);
    return this.http.post<any>(url, msgTemplate);
  }

  renderTxnTemplate(txnTemplate, transaction, orgId?: number): Observable<string> {
    let url = this.urlWithOrgId(`/solution/txn-types/render?transaction=${transaction}&orgId=`, orgId);
    let headers = new HttpHeaders();
    headers = headers.append('Content-type', 'text/plain; charset=utf-8');
    return this.http.post<string>(url, txnTemplate, {headers, responseType: 'text' as 'json'});
  }

  saveMessageTemplates(msgTemplates: MessageTemplate[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/message-templates?orgId=`, orgId);
    return this.http.post<QueryResult>(url, msgTemplates);
  }

  // pdf template data services

  getPdfTemplates(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/pdf-summary?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  savePdfTemplate(pdfTemplate: PdfTemplate, orgId?: number): Observable<PdfTemplate> {
    let url = `/solution/pdf-templates`;
    if (pdfTemplate.id) {
      url += this.urlWithOrgId(`/${pdfTemplate.id}?orgId=`, orgId);
      return this.http.put<PdfTemplate>(url, pdfTemplate);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<PdfTemplate>(url, pdfTemplate);
    }
  }

  deletePdfTemplate(pdfTemplate: PdfTemplate, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/pdf-templates/${pdfTemplate.id}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  // org file data services

  getOrgFiles(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/org-files?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveOrgFile(orgFile: OrgFile, orgId?: number): Observable<OrgFile> {
    let url = `/solution/org-files`;
    if (orgFile.id) {
      url += this.urlWithOrgId(`/${orgFile.id}?orgId=`, orgId);
      return this.http.put<OrgFile>(url, orgFile);
    } else {
      url += this.urlWithOrgId(`?orgId=`, orgId);
      return this.http.post<OrgFile>(url, orgFile);
    }
  }

  deleteOrgFile(orgFile: OrgFile, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/org-files/${orgFile.id}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  // ref code data services

  getRefCodeByName(refDomainId: number, name: string, orgId?: number): Observable<RefCode> {
    // return ref code that exactly matches the name
    let url = this.urlWithOrgId(`/solution/ref-code?refDomainId=${refDomainId}&name=${name}&orgId=`, orgId);
    return this.http.get<RefCode>(url);
  }

  getRefCodesByName(refDomainId: number, name: string, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/ref-codes?refDomainId=${refDomainId}&name=${name}&orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveRefCode(refCode: RefCode, orgId?: number): Observable<RefCode> {
    let url: string;

    if (refCode.id) {
      // watch out for the url - ref-codes for updating a ref code
      url = this.urlWithOrgId(`/solution/ref-codes/${refCode.id}?orgId=`, orgId);
      return this.http.put<RefCode>(url, refCode);
    } else {
      // watch out for the url - ref-code for posting a new ref code
      url = this.urlWithOrgId(`/solution/ref-code?orgId=`, orgId);
      return this.http.post<RefCode>(url, refCode);
    }
  }

  deleteRefCode(refCodeId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/ref-codes/${refCodeId}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  // ref domain data services

  getRefDomains(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/ref-domains?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveRefDomains(refData: RefDomain[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/ref-domains?orgId=`, orgId);
    return this.http.post<QueryResult>(url, refData);
  }

  // system param data services

  getSystemParams(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/sys-params?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }


  saveSystemParams(systemParms: SystemParam[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/sys-params?orgId=`, orgId);
    return this.http.post<QueryResult>(url, systemParms);
  }

  // approver  data services
  getApprovers(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/approvers?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }


  saveApprovers(approvers: ApproverPayload[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/approvers?orgId=`, orgId);
    return this.http.post<QueryResult>(url, approvers);
  }

  // to-do-lane data services

  getToDoLanes(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/todo-lanes?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveToDoLanes(toDoLanes: ToDoLane[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/todo-lanes?orgId=`, orgId);
    return this.http.post<QueryResult>(url, toDoLanes);
  }

  // txn type data services

  getTxnTypes(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/txn-types?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  /** Get Approvable Transaction Names for org
   *
   * @param escalatable if true, returns only escalatable transactions
   * @param orgId
   */
  getApprovableTxnNamesDropdown(escalatable?: boolean, orgId?: number): Observable<SelectItem[]> {

    let url = this.urlWithOrgId(`/solution/approvable-transactions?orgId=`, orgId);

    if (escalatable) {
      url = url + `&escalatable=true`;
    }

    return this.http.get<QueryResult>(url).pipe(map(res => {
      return this.transform.convertArrayToDropDown(res.list, false, null, NoTitlecasePartialTransactionType);
    }));
  }

  saveTxnTypes(txnTypes: TxnTypes[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/txn-types?orgId=`, orgId);
    return this.http.post<QueryResult>(url, txnTypes);
  }

  // workgroup data services

  getWorkgroups(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/workgroups?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveWorkgroups(workgroups: Workgroup[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/workgroups?orgId=`, orgId);
    return this.http.post<QueryResult>(url, workgroups);
  }

  // Org info services

  // ToDo jC: fix the data type it returns   Map<String, Boolean>
  checkAddrTypeAddrNameUniqueness(addressType: string, addressName: string, orgId?: number): Observable<Map<string, boolean>> {
    let url = this.urlWithOrgId(`/solution/orgunits/addresses/unique?&addrType=${addressType}&addrName=${addressName}&orgId=`, orgId);
    return this.http.get<Map<string, boolean>>(url);
  }

  getMainAndOtherOrgAddress(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/orgunits/addresses/main-and-other?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getOrgUnitById(orgId?: number): Observable<OrgUnit> {
    let url = this.urlWithOrgId(`/solution/orgunits?orgId=`, orgId);
    return this.http.get<OrgUnit>(url);
  }

  saveOrgUnit(orgUnit: OrgUnit, orgId?: number): Observable<OrgUnit> {
    let url = this.urlWithOrgId(`/solution/orgunits?orgId=`, orgId);
    return this.http.post<OrgUnit>(url, orgUnit);
  }

  createServiceUser(orgUnit: OrgUnit): Observable<void> {
    let url = this.urlWithOrgId(`/solution/create-service-user?realmToCreateUser=${orgUnit.realmName}&orgId=`, orgUnit.id);
    return this.http.get<void>(url);
  }

  deleteOrgUnitById(orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/orgunits?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  // Realm services

  getRealmsExceptDefault(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/realms?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getRealmById(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/realms?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveRealm(realm: Realm, orgId?: number): Observable<Realm> {
    let url = this.urlWithOrgId(`/solution/realms?orgId=`, orgId);
    return this.http.post<Realm>(url, realm);
  }

  saveClientsAndRolesAndGroupsToReam(realmId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/realms/clients-roles-groups?realmId=${realmId}&orgId=`, orgId);
    return this.http.post<Response>(url, {});

  }

  deleteRealmById(realmId: number, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/realms/${realmId}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  getSalesTaxSummaryByOrg(orgId: number): Observable<QueryResult> {
    let url = `/solution/sales-taxes?orgId=${orgId}`;
    return this.http.get<QueryResult>(url);
  }

  searchDefaultSalesTax(orgId: number): Observable<QueryResult> {
    let url = `/solution/default-sales-taxes/search?orgId=${orgId}`;
    return this.http.get<QueryResult>(url);
  }

  searchSalesTaxRateForOrg(name: string, orgId: number): Observable<QueryResult> {
    let url = `/solution/sales-taxes/search?orgId=${orgId}&name=${name}`;
    return this.http.get<QueryResult>(url);
  }

  getNearestSalesTaxRateByName(name: string, orgId: number): Observable<SalesTaxRate> {
    let url = `/solution/org-sales-taxes/name?orgId=${orgId}&name=${name}`;
    return this.http.get<SalesTaxRate>(url);
  }

  getSalesTaxRateNameUniqueness(name: string, id: number, orgId: number): Observable<Map<string,boolean>> {
    let url = ``;
    if (orgId) {
      url += `/solution/org-sales-taxes/unique?orgId=${orgId}&name=${name}`;
      if (id) {
        url += `&id=${id}`;
      }
    } else {
      url += `/procurement/sales-taxes/unique?name=${name}`;
    }
    return this.http.get<Map<string,boolean>>(url);
  }

  getSalesTaxAgencyUniqueness(name: string, id: number, orgId: number): Observable<Map<string,boolean>> {
    let url = '';
    console.log(orgId);
    if (orgId) {
      url += `/solution/org-sales-agencies/unique?orgId=${orgId}&taxAgency=${name}`;
    } else {
      url += `/procurement/sales-agencies/unique?taxAgency=${name}`;
    }
    if (id) {
      url += `&id=${id}`;
    }
    return this.http.get<Map<string,boolean>>(url);
  }

  deleteSalesTaxRateForDefaultOrg(rateId: number, orgId: number): Observable<Response> {
    let url = `/solution/sales-taxes/${rateId}?orgId=${orgId}`;
    return this.http.delete<Response>(url);
  }

  // support services

  getObs(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/directory/obs?start=0&count=-1&orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getObsAsDropdown(): Observable<SelectItem[]> {
    return this.getObs().pipe(map(res => {
      return this.transform.convertToDropDown("name", res.list, "id", true);
    }));
  }

  getEmployeeClass(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/hr/employee-classes/search?start=0&count=-1&orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getEmployeeClassAsDropdown(returnObjValue?: boolean): Observable<SelectItem[]> {

    if (returnObjValue) {
      return this.getEmployeeClass().pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list);
      }));
    } else {
      return this.getEmployeeClass().pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id");
      }));
    }
  }

  searchWorkGroups(workgroupName?: string, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/workgroups/search?start=0&count=-1&orgId=`, orgId);
    if (workgroupName) {
      url += `&name=${workgroupName}`;
    }
    return this.http.get<QueryResult>(url);
  }

  searchWorkGroupsAsDropdown(workgroupName?: string, returnObjValue?: boolean): Observable<SelectItem[]> {

    if (returnObjValue) {
      return this.searchWorkGroups(workgroupName).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list);
      }));
    } else {
      return this.searchWorkGroups(workgroupName).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id");
      }));
    }
  }

  searchTradingPartners(subtype: string, partnerName?: string, orgId?: number): Observable<QueryResult> {

    let url = this.urlWithOrgId(`/solution/procurement/trading-partners/search?start=0&count=-1&orgId=`, orgId);
    if (subtype) {
      url += `&subtype=${subtype}`;
    }
    if (partnerName) {
      url += `&businessName=${partnerName}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getTradingPartnerAsDropdown(subtype: string, partnerName?: string, returnObjValue?: boolean): Observable<SelectItem[]> {

    if (returnObjValue) {
      return this.searchTradingPartners(subtype, partnerName).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list);
      }));
    } else {
      return this.searchTradingPartners(subtype, partnerName).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id");
      }));
    }
  }

  //master catalog services

  getMasterCatalog(orgId?: number): Observable<ItemCatalog[]> {
    let url = this.urlWithOrgId(`/solution/system/master-catalog?orgId=`, orgId);
    return this.http.get<ItemCatalog[]>(url);
  }

  saveMasterCatalog(catalog: ItemCatalog, orgId?: number): Observable<ItemCatalog> {
    let url = this.urlWithOrgId(`/solution/system/item-catalog?orgId=`, orgId);
    return this.http.post<ItemCatalog>(url, catalog);
  }

  getDefaultItems(orgId?: number): Observable<AbstractItem[]> {
    let url = this.urlWithOrgId(`/solution/system/items/default-items?&orgId=`, orgId);
    return this.http.get<AbstractItem[]>(url);
  }

  saveDefaultItems(items: AbstractItem[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/system/items?&orgId=`, orgId);
    return this.http.post<QueryResult>(url, items);
  }

  getAccountingCloseTasks(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/accounting-close-task?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveAccountingCloseTasks(task: AccountingCloseTask, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/accounting-close-task?orgId=`, orgId);
    return this.http.post<QueryResult>(url, task);
  }

  deleteAccountingCloseTasks(task: AccountingCloseTask, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/accounting-close-task/${task.id}?orgId=`, orgId);
    return this.http.delete<QueryResult>(url);
  }

  getAssignmentHandlers(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/assignment-handler?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveAssignmentHandlers(assignmentHandlers: AssignmentHandler[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/assignment-handler?orgId=`, orgId);
    return this.http.post<QueryResult>(url, assignmentHandlers);
  }

  getComplianceAgencies(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/compliance-agencies?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveComplianceAgencies(complianceAgencies: ComplianceAgency[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/compliance-agencies?orgId=`, orgId);
    return this.http.post<QueryResult>(url, complianceAgencies);
  }

  getTimesheetPayType(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/timesheet-pay-type?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveTimesheetPayType(complianceAgencies: ComplianceAgency[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/timesheet-pay-type?orgId=`, orgId);
    return this.http.post<QueryResult>(url, complianceAgencies);
  }

  getDefaultFilePermissions(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/file-settings/default?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getFileSecurityRoleConfig(orgId: number, transaction: string): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/file-settings/role-config?transaction=${transaction}&orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  saveDefaultFilePermissions(settings: DefaultPermissionSetting[], orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/file-settings/default?orgId=`, orgId);
    return this.http.put<QueryResult>(url, settings);
  }

  //TODO: Refactor into Ach or Solution Manager Service
  createBeneficialOwner(owner: OrgBeneficialOwner, orgId?: number): Observable<OrgBeneficialOwner> {
    let url = this.urlWithOrgId(`/solution/beneficial-owner?orgId=`, orgId);
    return this.http.post<OrgBeneficialOwner>(url, owner);
  }

  //TODO: Refactor into Ach or Solution Manager Service
  refreshBeneficialOwner(owner: OrgBeneficialOwner, orgId?: number): Observable<OrgBeneficialOwner> {
    let url = this.urlWithOrgId(`/solution/beneficial-owner/refresh?orgId=`, orgId);
    return this.http.put<OrgBeneficialOwner>(url, owner.dwollaId);
  }

  updateBeneficialOwner(owner: OrgBeneficialOwner, orgId?: number): Observable<OrgBeneficialOwner> {
    let url = this.urlWithOrgId(`/solution/beneficial-owner?orgId=`, orgId);
    return this.http.put<OrgBeneficialOwner>(url, owner);
  }

  deleteBeneficialOwner(owner: OrgBeneficialOwner, orgId?: number): Observable<Response> {
    let url = `/solution/beneficial-owner/${owner.id}?orgId=`;
    let finalUrl = this.urlWithOrgId(url, orgId);
    return this.http.delete<Response>(finalUrl);
  }

  certifyAchInformation(achCustomer: AchCustomer, orgId?: number): Observable<AchCustomer> {
    let url = this.urlWithOrgId(`/solution/ach/certify?orgId=`, orgId);
    return this.http.post<AchCustomer>(url, achCustomer);
  }

  createVerifiedAchCustomer(orgUnit: OrgUnit, orgId?: number): Observable<OrgUnit> {
    let url = this.urlWithOrgId(`/solution/verified-customer?orgId=`, orgId);
    return this.http.post<OrgUnit>(url, orgUnit);
  }

  saveAchDetail(achDetail: OrgAchDetail | PartnerAchDetail, orgId?: number): Observable<OrgAchDetail> {
    let url = this.urlWithOrgId(`/solution/org-unit/ach-detail?orgId=`, orgId);
    return this.http.post<OrgAchDetail>(url, achDetail);
  }

  deleteAchDetail(achDetail: OrgAchDetail | PartnerAchDetail, orgId?: number): Observable<Response> {
    let url = this.urlWithOrgId(`/solution/org-unit/ach-detail/${achDetail.id}?orgId=`, orgId);
    return this.http.delete<Response>(url);
  }

  updateAchDetail(achDetail: OrgAchDetail | PartnerAchDetail, orgId?: number): Observable<OrgAchDetail> {
    let url = this.urlWithOrgId(`/solution/org-unit/ach-detail/${achDetail.id}?orgId=`, orgId);
    return this.http.put<OrgAchDetail>(url, achDetail);
  }

  getOrgAchDetail(orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/org-unit/${orgId}/ach-details?orgId=`, orgId);
    return this.http.get<QueryResult>(url);
  }

  getValidationSummary(validationCategoryTypes: ValidationCategoryType[], orgId?: number): Observable<SettingValidationSummary> {
    let query: string = this.transform.buildObjectAsListURL("validationCategories", validationCategoryTypes);
    let url = this.urlWithOrgId(`/solution/validation-summary?` + query + `&orgId=`, orgId);
    return this.http.get<SettingValidationSummary>(url);
  }

  getTxnDetails(txnType: TxnType, txnNumber?: string, orgId?: number): Observable<ApprovableTransaction[]> {
    let url;
    if (txnNumber) {
      url = this.urlWithOrgId(`/solution/approvable-txns?transaction=${txnType}&txnNumber=${txnNumber}&orgId=`, orgId);
    } else {
      url = this.urlWithOrgId(`/solution/approvable-txns?transaction=${txnType}&orgId=`, orgId);
    }
    return this.http.get<ApprovableTransaction[]>(url);
  }

  runValidation(refId: number, txnType: TxnType, orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/validate-transaction?transaction=${txnType}&refId=${refId}&orgId=`, orgId);
    return this.http.get<any>(url);
  }

  submitAndApproveInvoice(transaction: TxnType, refId: number): Observable<Response> {
    let url = `/approval/worklist/approve-transaction?transaction=${transaction}&refId=${refId}`;
    return this.http.post<Response>(url, {});
  }


  resetApproval(txnType: TxnType, refId: number, resetReason: string, orgId?:number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/reset-approvals?transaction=${txnType}&refId=${refId}&responseMessage=${resetReason}&orgId=`, orgId);
    return this.http.post<any>(url, {});
  }

  reAssignApproval(txnType: Transaction, refId: number, approvalSummary: WorkitemApprovalSummary,  orgId?:number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/reassign-approvals?transaction=${txnType}&refId=${refId}&orgId=`, orgId);
    return this.http.post<any>(url, approvalSummary);
  }

  checkIfReAssignmentIsEnabled(orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution/check-reassign-approvals?orgId=`, orgId);
    return this.http.get<any>(url);
  }

  getAccountingSettings(): Observable<AccountSetting[]> {
    let url = this.urlWithOrgId(`/solution/account-settings?orgId=`);
    return this.http.get<AccountSetting[]>(url);
  }

  getProjectSummary(): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/projects?orgId=`);
    return this.http.get<QueryResult>(url);
  }

  getProjectTasks(projectId: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/project-tasks?projectId=${projectId}&orgId=`);
    return this.http.get<QueryResult>(url);
  }

  getProjectTaskBalances(projectTaskId: number, inSolutionManager: boolean): Observable<QueryResult> {
    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/project-task-balance?projectTaskId=${projectTaskId}&orgId=`);
    } else {
      url = `/projects/project-task-balance?projectTaskId=${projectTaskId}`;
    }
    return this.http.get<QueryResult>(url);
  }

  saveProjectTaskBalance(projectTaskBalance: ProjectTaskBalance, inSolutionManager: boolean): Observable<ProjectTaskBalance> {
    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/project-task-balance?orgId=`);
    } else {
      url = `/projects/project-task-balance`;
    }
    return this.http.post<ProjectTaskBalance>(url, projectTaskBalance);
  }

  updateProjectTaskBalance(projectTaskBalance: ProjectTaskBalance, inSolutionManager: boolean): Observable<ProjectTaskBalance> {

    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/project-task-balance/${projectTaskBalance.id}?orgId=`);
    } else {
      url = `/projects/project-task-balance/${projectTaskBalance.id}`;
    }
    return this.http.put<ProjectTaskBalance>(url, projectTaskBalance);
  }

  rollUpProjectTaskBalance(projectTaskBalance: ProjectTaskBalance): Observable<ProjectTaskBalance> {
    let url = `/projects/project-task-balance/${projectTaskBalance.id}/rollup`;
    return this.http.put<ProjectTaskBalance>(url, {});
  }

  getFirmFixedInitialRevenue(clinId: number, projectId: number, inSolutionManager: boolean): Observable<ProjectRevenueBalance> {
    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/initial-revenue-firm-fixed?orgId=`);
    } else {
      url = `/projects/initial-revenue-firm-fixed?count=-1`;
    }
    if (clinId) {
      url += `&clinId=${clinId}`;
    } else if (projectId) {
      url += `&projectId=${projectId}`;
    }
    return this.http.get<ProjectRevenueBalance>(url);
  }

  getOtherInitialRevenue(clinId: number, projectId: number, inSolutionManager: boolean): Observable<ProjectRevenueBalance[]> {
    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/initial-revenue-others?orgId=`);
    } else {
      url = `/projects/initial-revenue-others?count=-1`;
    }
    if (clinId) {
      url += `&clinId=${clinId}`;
    }
    if (projectId) {
      url += `&projectId=${projectId}`;
    }
    return this.http.get<ProjectRevenueBalance[]>(url);
  }

  saveInitialRevenue(revenue: ProjectRevenueBalance, inSolutionManager: boolean): Observable<ProjectRevenueBalance> {
    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/initial-revenue?orgId=`);
    } else {
      url = `/projects/initial-revenue`;
    }
    return this.http.post<ProjectRevenueBalance>(url, revenue);
  }

  updateInitialRevenue(revenue: ProjectRevenueBalance, inSolutionManager: boolean): Observable<ProjectRevenueBalance> {

    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/initial-revenue/${revenue.id}?orgId=`);
    } else {
      url = `/projects/initial-revenue/${revenue.id}`;
    }
    return this.http.put<ProjectRevenueBalance>(url, revenue);
  }

  saveOrUpdateRevenues(revenues: ProjectRevenueBalance[], inSolutionManager: boolean): Observable<ProjectRevenueBalance[]> {

    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/initial-revenues?orgId=`);
    } else {
      url = `/projects/initial-revenues`;
    }
    return this.http.post<ProjectRevenueBalance[]>(url, revenues);
  }

  getAccountingCalendarBeforeGoLive(subType: CalendarType, inSolutionManager: boolean): Observable<Calendar> {
    let url;
    if (inSolutionManager) {
      url = this.urlWithOrgId(`/solution/project-task-balance/accounting-before-go-live?subtype=${subType}&orgId=`);
    } else {
      url = `/calendar/accounting-before-go-live?subtype=${subType}`;
    }
    return this.http.get<Calendar>(url);
  }

  getCalendarYears(subtype: CalendarType, tillToday?: boolean, laborOrExpense?: boolean, orgId?: number): Observable<IdNamePair[]> {
    let url = this.urlWithOrgId(`/solution/calendar/years?subtype=${subtype}&orgId=`);

    if (tillToday != null) {
      url += `&tillToday=${String(tillToday)}`;
    } else {
      url += `&tillToday=true`;
    }
    if (laborOrExpense != null) {
      url += `&laborOrExpense=${String(laborOrExpense)}`;
    }
    return this.http.get<IdNamePair[]>(url);
  }

  getCalendarYearsAsDropdown(subtype: CalendarType, sortDescending?: boolean, tillToday?: boolean, laborOrExpense?: boolean, orgId?: number): Observable<SelectItem<string>[]> {
    return this.getCalendarYears(subtype, tillToday, laborOrExpense, orgId).pipe(map(res => {
      let calendars = plainToClass(IdNamePair, res);

      if (sortDescending) {
        let dropdown = <SelectItem<string>[]>this.transform.convertToDropDown("name", calendars, "id");
        return dropdown.sort((a, b) => a.value > b.value ? -1 : a.value < b.value ? 1 : 0);
      } else {
        return <SelectItem<string>[]>this.transform.convertToDropDown("name", calendars, "id");
      }
    }));
  }

  searchCalendar(calendarType: CalendarType, year?: string, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/calendars/search?subtype=${calendarType}&orgId=`, orgId);
    if (year) {
      url += `&year=${year}`;
    }
    return this.http.get<QueryResult>(url);
  }

  searchCalendarAsDropdown(calendarType: CalendarType, year?: string, orgId?: number): Observable<SelectItem[]> {
    return this.searchCalendar(calendarType, year, orgId).pipe(map(res => {
      return this.transform.convertCalendarToPeriodDropdown(res.list);
    }));
  }

  searchInheritedCalendar(calendarType: CalendarType, year?: string, orgId?: number): Observable<QueryResult> {
    let url = this.urlWithOrgId(`/solution/calendars/inherited-search?subtype=${calendarType}&orgId=`, orgId);
    if (year) {
      url += `&year=${year}`;
    }
    return this.http.get<QueryResult>(url);
  }

  searchInheritedCalendarAsDropdown(calendarType: CalendarType, year?: string, sortDescending?: boolean, orgId?: number): Observable<SelectItem[]> {
    return this.searchInheritedCalendar(calendarType, year, orgId).pipe(map(res => {
      let calendars = plainToClass(Calendar, res.list);

      if (sortDescending) {
        let dropdown = <SelectItem<string>[]>this.transform.convertToDropDown("name", calendars, "id");
        return dropdown.sort((a, b) => a.value > b.value ? -1 : a.value < b.value ? 1 : 0);
      } else {
        return <SelectItem<string>[]>this.transform.convertToDropDown("name", calendars, "id");
      }
    }));
  }

  getAdminServices(orgId?: number): Observable<AdminServiceInfo[]> {
    let url = this.urlWithOrgId(`/solution/admin-services?orgId=`, orgId);
    return this.http.get<AdminServiceInfo[]>(url);
  }

  getPerDiemRates(orgId?: number): Observable<PerDiemRate[]> {
    let url = this.urlWithOrgId(`/solution/perdiem-rates?orgId=`, orgId);
    return this.http.get<PerDiemRate[]>(url);
  }

  importPerDiemRates(isOconus: boolean, templateFile: DocFile, effectiveDate?: string, orgId?: number): Observable<PerDiemRate[]> {
    let url =  this.urlWithOrgId(`/solution/perdiem-rates?orgId=`, orgId);
    if(isOconus){
      url += `&isOconus=true`;
    } else {
      url += `&isOconus=false`;
    }

    if(effectiveDate){
      url+=`&effectiveFrom=${effectiveDate}`;
    }

    return this.http.post<PerDiemRate[]>(url, templateFile);
  }

  invokeAdminService(adminService: AdminServiceInfo, orgId?: number): Observable<any> {
    let url = this.urlWithOrgId(`/solution${adminService.url}?orgId=`, orgId);
    let body = null;

    if (adminService.query) {
      url += "&" + adminService.query;
    }
    if (adminService.body) {
      body = adminService.body;
    }

    // ToDo: for put and delete, need to handle id


    if (adminService.httpMethod == HttpMethod.Get) {
      return this.http.get<any>(url);
    } else if (adminService.httpMethod == HttpMethod.Put) {
      return this.http.put<any>(url, body);
    } else if (adminService.httpMethod == HttpMethod.Post) {
      return this.http.post<any>(url, body);
    } else if (adminService.httpMethod == HttpMethod.Delete) {
      return this.http.delete<any>(url);
    } else {
      console.error ("Unknown HTTP method!");
    }
  }

  getLeaveInfoForPeriod(timesheetPeriodId: number, orgId?: number): Observable<LeaveProcessingInfo[]> {
    let url =  this.urlWithOrgId(`/solution/reconcile-leave/${timesheetPeriodId}?orgId=`, orgId);
    return this.http.get<LeaveProcessingInfo[]>(url);
  }

  reconcileLeaveInfo(leaveInfo: LeaveProcessingInfo, orgId?: number): Observable<LeaveProcessingInfo> {
    let url =  this.urlWithOrgId(`/solution/reconcile-leave?orgId=`, orgId);
    return this.http.post<LeaveProcessingInfo>(url, leaveInfo);
  }

  getUnProcessedEmployeeLeaveDetails(timesheetPeriodId: number, orgId?: number): Observable<LeaveDetail[]> {
    let url =  this.urlWithOrgId(`/solution/reconcile-leave/${timesheetPeriodId}/un-processed?orgId=`, orgId);

    return this.http.get<LeaveDetail[]>(url);
  }
  processReconciledLeave(timesheetPeriodId: number, reason: string, orgId?: number): Observable<any> {
    let url =  this.urlWithOrgId(`/solution/reconcile-leave/${timesheetPeriodId}/process?orgId=`, orgId);
    url += `&reason=${reason}`
    return this.http.post<any>(url, {});
  }


}
